import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import cn from 'classnames';
import CustomSelect from '../../CustomSelect';
import Field from '../Field';
import { getCathedraRetraining } from '../../../actions/cathedras';
import styles from '../card.module.css';

/* Вкладка «Дополнительная учебная работа» (п. 9 ТЗ).

   Сверху — «Учебный год» (перечень с сервера, по умолчанию текущий). Смена года
   перезапрашивает показатели и не сбрасывает выбранную внутреннюю вкладку.
   Внутренние вкладки 1–5 — таблица «Показатель»/«Значение» с группами-заголовками
   (is_header) и вложенными строками (level). Счётчик считает только показатели со
   значением. Ноль — заданное значение, выводится цифрой «0».

   Внутренняя вкладка «Переподготовка и ПК» — свой запрос и свой календарный год;
   при этом «Учебный год» сверху становится неактивным (как в десктопе, US-44). */

const SUBTABS = [
  { key: 'st', label: 'Штатный состав', group: 'Штатный состав' },
  { key: 'ex', label: 'Экзамены', group: 'Экзамены' },
  { key: 'rc', label: 'Рецензирование', group: 'Рецензирование' },
  { key: 'ld', label: 'Загрузка', group: 'Загрузка' },
  { key: 'hl', label: 'Часовая загрузка', group: 'Часовая загрузка' },
  { key: 'pk', label: 'Переподготовка и ПК' },
];

const none = (title, text) => (
  <div className={styles.empty}>
    <div className={styles.emptyTitle}>{title}</div>
    {text}
  </div>
);

// --------- Внутренняя вкладка «Переподготовка и ПК» (свой год и запрос) ---------
const RetrainingPanel = ({ id }) => {
  const dispatch = useDispatch();
  const [store, setStore] = useState({});
  const [calYear, setCalYear] = useState(null);
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  const key = calYear || '';
  const entry = store[key];

  useEffect(() => {
    if (entry) return;
    setStore(prev => ({ ...prev, [key]: { status: 'loading' } }));
    (async () => {
      const result = await dispatch(getCathedraRetraining(id, calYear));
      if (!aliveRef.current) return;
      setStore(prev => ({
        ...prev,
        [key]: result.error ? { status: 'error', error: result.error } : { status: 'ok', data: result.data },
      }));
    })();
  }, [dispatch, entry, key, id, calYear]);

  // перечень лет одинаков — берётся из любого уже полученного ответа
  const years = Object.keys(store)
    .map(k => store[k].data && store[k].data.years)
    .find(Boolean) || [];

  const data = entry && entry.status === 'ok' ? entry.data : null;
  const selected = calYear != null ? calYear : (data ? data.year : null);

  return (
    <div>
      <div className={styles.yearField}>
        <CustomSelect
          title="На календарный год"
          width="100%"
          withoutAll
          options={years.map(y => ({ value: String(y), name: String(y) }))}
          value={selected == null ? '' : String(selected)}
          setFieldValue={(name, value) => setCalYear(value)}
          valuesName="calYear"
        />
      </div>

      {!data ? (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className={styles.fform}>
          <Field label="План по переподготовке постоянного состава" value={data.plan_retraining} mono />
          <Field
            label="План по повышению квалификации постоянного состава"
            value={data.plan_qualification}
            mono
          />
        </div>
      )}
    </div>
  );
};

// --------- Таблица показателей внутренней вкладки ---------
const IndicatorsTable = ({ group }) => {
  const indicators = (group && group.indicators) || [];
  if (!indicators.length) {
    return none('Показателей нет', 'На выбранный учебный год значения не заданы');
  }
  const withValue = indicators.filter(item => item.value != null).length;

  return (
    <>
      <div className={styles.gridCard}>
        <div className={styles.gridScroll}>
          <table className={styles.gt}>
            <thead>
              <tr>
                <th>Показатель</th>
                <th>Значение</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((item, index) => (item.is_header ? (
                <tr key={item.id != null ? `${item.id}-${index}` : index} className={styles.grpRow}>
                  <td colSpan={2}>{item.name}</td>
                </tr>
              ) : (
                <tr key={item.id != null ? `${item.id}-${index}` : index}>
                  <td className={cn(styles.lead, { [styles.indent]: item.level > 0 })}>
                    {item.name}
                  </td>
                  <td className={styles.mono}>
                    {item.value == null ? <span className={styles.dash}>—</span> : String(item.value)}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>
      <div className={styles.counter}>Всего показателей: <b>{withValue}</b></div>
    </>
  );
};

const AdditionalWork = ({ id, data, years, year, loading, onYearChange }) => {
  const [wtab, setWtab] = useState('st');

  const groupByName = useMemo(() => {
    const map = {};
    ((data && data.groups) || []).forEach((g) => { map[g.name] = g; });
    return map;
  }, [data]);

  const groups = (data && data.groups) || [];
  const isPk = wtab === 'pk';

  const renderContent = () => {
    if (isPk) return <RetrainingPanel id={id} />;
    if (loading || !data) {
      return (
        <div className={styles.loader}>
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading...</span>
          </Spinner>
        </div>
      );
    }
    if (!groups.length) {
      return none('Показателей нет', 'На выбранный учебный год значения не заданы');
    }
    const sub = SUBTABS.find(item => item.key === wtab);
    // сопоставление по имени группы, запасной вариант — по порядку внутренних вкладок
    const group = groupByName[sub.group]
      || groups[SUBTABS.findIndex(item => item.key === wtab)];
    return <IndicatorsTable group={group} />;
  };

  return (
    <div className={styles.pane}>
      <div className={cn(styles.yearField, { [styles.yearFieldOff]: isPk })}>
        <CustomSelect
          title="Учебный год"
          width="100%"
          withoutAll
          options={(years || []).map(item => ({ value: String(item.id), name: item.name }))}
          value={year == null ? '' : String(year)}
          setFieldValue={(name, value) => onYearChange(Number(value))}
          valuesName="schoolYear"
          disabled={isPk}
        />
      </div>

      <div className={styles.subtabs}>
        {SUBTABS.map(item => (
          <button
            key={item.key}
            type="button"
            className={cn({ [styles.subtabActive]: item.key === wtab })}
            onClick={() => setWtab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {renderContent()}
    </div>
  );
};

export default AdditionalWork;
